import { useState } from 'react';
import { useAuth } from '@/auth/AuthContext';

const API = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

const T = {
  agniCore:'#EA580C', agniGlow:'#F97316', vidyaGold:'#C8A84B',
  nightInk:'#0F1A2E', slate:'#111827', muted:'#6B7280',
  border:'#E5E7EB', surface:'#F9FAFB', green:'#16A34A',
};

const ROLE_META = {
  superadmin: { label:'Super Admin', color:'#7C3AED', bg:'#F5F3FF' },
  admin:      { label:'Admin',       color:'#EA580C', bg:'#FFF7ED' },
  teacher:    { label:'Teacher',     color:'#2563EB', bg:'#EFF6FF' },
  parent:     { label:'Parent',      color:'#059669', bg:'#ECFDF5' },
};

function Avatar({ user, size = 80 }) {
  const initials = (user?.name || 'U').split(' ').map(w => w[0]).slice(0,2).join('').toUpperCase();
  if (user?.avatar) {
    return <img src={user.avatar} alt={user.name} style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover' }}/>;
  }
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:`linear-gradient(135deg,${T.agniCore},${T.agniGlow})`,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:size*0.35, fontWeight:900, color:'#fff', fontFamily:"'Nunito',sans-serif", flexShrink:0 }}>
      {initials}
    </div>
  );
}

export default function ProfilePage() {
  const { user, getAuthHeader, login } = useAuth();

  // Profile edit state
  const [name,        setName]        = useState(user?.name || '');
  const [saving,      setSaving]      = useState(false);
  const [saveMsg,     setSaveMsg]     = useState(null); // {type:'success'|'error', text}

  // Password change state
  const [curPw,       setCurPw]       = useState('');
  const [newPw,       setNewPw]       = useState('');
  const [confirmPw,   setConfirmPw]   = useState('');
  const [showCur,     setShowCur]     = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwSaving,    setPwSaving]    = useState(false);
  const [pwMsg,       setPwMsg]       = useState(null);

  const hasLocal  = user?.authMethods?.includes('local');
  const hasGoogle = user?.authMethods?.includes('google');
  const roleMeta  = ROLE_META[user?.role] || { label: user?.role, color: T.muted, bg: T.surface };

  const handleSaveProfile = async () => {
    if (!name.trim()) return;
    setSaving(true); setSaveMsg(null);
    try {
      const res  = await fetch(`${API}/auth/profile`, {
        method: 'PATCH',
        headers: getAuthHeader(),
        body: JSON.stringify({ name: name.trim() }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setSaveMsg({ type:'success', text:'Profile updated.' });
      // Refresh user in context by re-fetching /me
      await fetch(`${API}/auth/me`, { headers: getAuthHeader() });
    } catch (e) {
      setSaveMsg({ type:'error', text: e.message });
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!curPw || !newPw || !confirmPw) { setPwMsg({ type:'error', text:'All fields are required.' }); return; }
    if (newPw.length < 8) { setPwMsg({ type:'error', text:'New password must be at least 8 characters.' }); return; }
    if (newPw !== confirmPw) { setPwMsg({ type:'error', text:'New passwords do not match.' }); return; }
    setPwSaving(true); setPwMsg(null);
    try {
      const res  = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({ currentPassword: curPw, newPassword: newPw }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setPwMsg({ type:'success', text:'Password changed successfully.' });
      setCurPw(''); setNewPw(''); setConfirmPw('');
    } catch (e) {
      setPwMsg({ type:'error', text: e.message });
    }
    setPwSaving(false);
  };

  return (
    <div style={{ padding:'32px 32px', maxWidth:780, margin:'0 auto', fontFamily:"'Inter',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Inter:wght@400;500;600&display=swap');
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        .prof-input{width:100%;border:1.5px solid ${T.border};border-radius:10px;padding:11px 14px;font-size:14px;font-family:'Inter',sans-serif;outline:none;transition:border-color .18s,box-shadow .18s;background:#FAFAFA;color:${T.slate};box-sizing:border-box}
        .prof-input:focus{border-color:${T.agniCore};box-shadow:0 0 0 3px rgba(234,88,12,.1);background:#fff}
        .prof-input:disabled{background:#F3F4F6;color:#9CA3AF;cursor:not-allowed}
        .prof-btn{padding:10px 24px;border:none;border-radius:50px;font-size:14px;font-weight:700;font-family:'Inter',sans-serif;cursor:pointer;transition:background .18s,transform .12s,box-shadow .15s}
        .prof-btn-primary{background:${T.agniCore};color:#fff}
        .prof-btn-primary:hover:not(:disabled){background:#C2410C;transform:translateY(-1px);box-shadow:0 6px 18px rgba(234,88,12,.3)}
        .prof-btn-primary:disabled{background:#F9A075;cursor:not-allowed}
        .eye-btn{position:absolute;right:12px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;padding:4px;color:#9CA3AF;font-size:15px}
        .eye-btn:hover{color:${T.agniCore}}
        .card{background:#fff;border-radius:16px;border:1.5px solid ${T.border};padding:28px 32px;margin-bottom:20px;animation:fadeUp .4s ease both}
      `}</style>

      {/* Page header */}
      <div style={{ marginBottom:28 }}>
        <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:26, fontWeight:900, color:T.nightInk, marginBottom:4 }}>My Profile</div>
        <div style={{ fontSize:14, color:T.muted }}>Manage your account details and security settings.</div>
      </div>

      {/* Profile card */}
      <div className="card" style={{ animationDelay:'0s' }}>
        <div style={{ display:'flex', alignItems:'center', gap:20, marginBottom:28, paddingBottom:24, borderBottom:`1px solid ${T.border}` }}>
          <Avatar user={user} size={72}/>
          <div style={{ flex:1 }}>
            <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:22, fontWeight:900, color:T.nightInk, lineHeight:1.2 }}>{user?.name}</div>
            <div style={{ fontSize:14, color:T.muted, marginTop:3 }}>{user?.email}</div>
            <div style={{ display:'flex', gap:8, marginTop:8, flexWrap:'wrap' }}>
              <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:roleMeta.bg, color:roleMeta.color, fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20, border:`1px solid ${roleMeta.color}22` }}>
                {roleMeta.label}
              </span>
              {hasGoogle && (
                <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#EFF6FF', color:'#2563EB', fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>
                  🔗 Google
                </span>
              )}
              {hasLocal && (
                <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:'#F0FDF4', color:'#16A34A', fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:20 }}>
                  🔑 Password
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Editable fields */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:24 }}>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#374151', marginBottom:6, letterSpacing:.8 }}>FULL NAME</label>
            <input className="prof-input" value={name} onChange={e=>setName(e.target.value)} placeholder="Your name"/>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#374151', marginBottom:6, letterSpacing:.8 }}>EMAIL ADDRESS</label>
            <input className="prof-input" value={user?.email || ''} disabled/>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#374151', marginBottom:6, letterSpacing:.8 }}>ROLE</label>
            <input className="prof-input" value={roleMeta.label} disabled/>
          </div>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#374151', marginBottom:6, letterSpacing:.8 }}>SIGN-IN METHOD</label>
            <input className="prof-input" value={[hasGoogle && 'Google', hasLocal && 'Password'].filter(Boolean).join(' + ')} disabled/>
          </div>
        </div>

        {saveMsg && (
          <div style={{ marginBottom:16, padding:'10px 14px', borderRadius:9, fontSize:13, fontWeight:600,
                        background: saveMsg.type==='success'?'#F0FDF4':'#FFF1F0',
                        color:      saveMsg.type==='success'?'#166534':'#B91C1C',
                        border:     `1px solid ${saveMsg.type==='success'?'#86EFAC':'#FECACA'}` }}>
            {saveMsg.type==='success'?'✓ ':' ⚠ '}{saveMsg.text}
          </div>
        )}

        <div style={{ display:'flex', justifyContent:'flex-end' }}>
          <button className="prof-btn prof-btn-primary" onClick={handleSaveProfile}
            disabled={saving || name.trim()===user?.name}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Change password card */}
      <div className="card" style={{ animationDelay:'.08s' }}>
        <div style={{ fontFamily:"'Nunito',sans-serif", fontSize:18, fontWeight:900, color:T.nightInk, marginBottom:4 }}>Change Password</div>
        <div style={{ fontSize:13, color:T.muted, marginBottom:24 }}>
          {hasLocal
            ? 'Update your account password. You will remain logged in.'
            : 'Your account uses Google sign-in only. You cannot set a password.'}
        </div>

        {!hasLocal ? (
          <div style={{ display:'flex', alignItems:'center', gap:12, padding:'14px 18px', background:'#EFF6FF', borderRadius:12, border:'1px solid #BFDBFE' }}>
            <span style={{ fontSize:22 }}>🔗</span>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'#1D4ED8' }}>Google sign-in account</div>
              <div style={{ fontSize:12, color:'#3B82F6', marginTop:2 }}>Password management is handled by Google. Sign in via "Continue with Google" on the login page.</div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16, marginBottom:20 }}>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#374151', marginBottom:6, letterSpacing:.8 }}>CURRENT PASSWORD</label>
                <div style={{ position:'relative' }}>
                  <input className="prof-input" type={showCur?'text':'password'} value={curPw} onChange={e=>setCurPw(e.target.value)} placeholder="••••••••" style={{ paddingRight:40 }}/>
                  <button className="eye-btn" type="button" onClick={()=>setShowCur(p=>!p)}>{showCur?'🙈':'👁️'}</button>
                </div>
              </div>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#374151', marginBottom:6, letterSpacing:.8 }}>NEW PASSWORD</label>
                <div style={{ position:'relative' }}>
                  <input className="prof-input" type={showNew?'text':'password'} value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="Min 8 characters" style={{ paddingRight:40 }}/>
                  <button className="eye-btn" type="button" onClick={()=>setShowNew(p=>!p)}>{showNew?'🙈':'👁️'}</button>
                </div>
              </div>
              <div>
                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#374151', marginBottom:6, letterSpacing:.8 }}>CONFIRM PASSWORD</label>
                <div style={{ position:'relative' }}>
                  <input className="prof-input" type={showConfirm?'text':'password'} value={confirmPw} onChange={e=>setConfirmPw(e.target.value)} placeholder="Repeat new password" style={{ paddingRight:40 }}/>
                  <button className="eye-btn" type="button" onClick={()=>setShowConfirm(p=>!p)}>{showConfirm?'🙈':'👁️'}</button>
                </div>
              </div>
            </div>

            {/* Password strength hint */}
            {newPw.length > 0 && (
              <div style={{ marginBottom:16, display:'flex', gap:6, alignItems:'center' }}>
                {[1,2,3,4].map(i => {
                  const strength = [newPw.length>=8, /[A-Z]/.test(newPw), /[0-9]/.test(newPw), /[^A-Za-z0-9]/.test(newPw)].filter(Boolean).length;
                  const colors = ['#EF4444','#F97316','#EAB308','#22C55E'];
                  return <div key={i} style={{ height:4, flex:1, borderRadius:4, background: i<=strength ? colors[strength-1] : T.border, transition:'background .2s' }}/>;
                })}
                <span style={{ fontSize:11, color:T.muted, fontWeight:600, whiteSpace:'nowrap' }}>
                  {['','Weak','Fair','Good','Strong'][[newPw.length>=8, /[A-Z]/.test(newPw), /[0-9]/.test(newPw), /[^A-Za-z0-9]/.test(newPw)].filter(Boolean).length]}
                </span>
              </div>
            )}

            {pwMsg && (
              <div style={{ marginBottom:16, padding:'10px 14px', borderRadius:9, fontSize:13, fontWeight:600,
                            background: pwMsg.type==='success'?'#F0FDF4':'#FFF1F0',
                            color:      pwMsg.type==='success'?'#166534':'#B91C1C',
                            border:     `1px solid ${pwMsg.type==='success'?'#86EFAC':'#FECACA'}` }}>
                {pwMsg.type==='success'?'✓ ':'⚠ '}{pwMsg.text}
              </div>
            )}

            <div style={{ display:'flex', justifyContent:'flex-end' }}>
              <button className="prof-btn prof-btn-primary" onClick={handleChangePassword}
                disabled={pwSaving || !curPw || !newPw || !confirmPw}>
                {pwSaving ? 'Updating…' : 'Update Password'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
